import { DrawerProps } from 'components/Drawer/Root'
import Styles from './BackgroundsDrawer.module.scss'
import { ImageButtons } from './ImageButtons'
import { ColorsRow } from './ColorsRow'
import { ColorsPanelContainer } from 'components/Design/ColorsPanel/ColorsPanelContainer'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { DesignerDrawer } from 'components/Design/DesignerDrawer/DesignerDrawer'
import { BackgroundsTabProvider, useBackgroundsTab } from '../BackgroundsTabContext'
import { Suggestions } from '../Suggestions'
import { Search } from '../Search'
import { createBackground } from '../createBackground'
import { BackgroundAssetProps } from '../Types'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { CategoryPanelProps } from '../CategoryPanel'
import { useEffect, useState } from 'react'
import { InnerDrawer, titles } from 'components/Design/UploadsTab/MobileUploads/UploadsDrawer'
import { UploadsTabProvider, useUploadsTab } from 'components/Design/UploadsTab/UploadsTabContext'

const BackgroundsDrawer = observer((props: DrawerProps) => {
  const { designStore, drawersStore } = useRootStore()
  const editor = useEditor()
  const {
    openPanel: openBackgroundsPanel,
    panelProps: backgroundPanelProps,
    panel: backgroundsPanel,
    closePanel: closeBackgroundsPanel,
  } = useBackgroundsTab()
  const { panel: uploadsPanel, closePanel: closeUploadsPanel } = useUploadsTab()
  const [showUploadsTab, setShowUploadsTab] = useState(false)

  useEffect(() => {
    resetDrawer()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const resetDrawer = () => {
    designStore?.closePanel()
    setShowUploadsTab(false)
    closeUploadsPanel()
    closeBackgroundsPanel()
  }

  const onBack = () => {
    if (uploadsPanel !== 'default') {
      closeUploadsPanel()
      return
    }

    resetDrawer()
  }

  const onClose = () => {
    resetDrawer()
    drawersStore.closeDrawer()
  }

  const texturePanelProps = backgroundPanelProps as CategoryPanelProps
  const showTexture = backgroundsPanel === 'category'
  const showColors = designStore?.panel?.type === 'colors'
  const handleBack = showColors || showTexture || showUploadsTab ? onBack : undefined

  let drawerTitle = 'Backgrounds'
  if (showTexture) drawerTitle = texturePanelProps.name
  if (showColors) drawerTitle = 'Colors & Gradients'
  if (showUploadsTab) drawerTitle = uploadsPanel === 'default' ? 'Image & Video' : titles[uploadsPanel]

  const onTextureSelect = (assetProps: BackgroundAssetProps) => {
    createBackground(editor, assetProps)
  }

  return (
    <DesignerDrawer title={drawerTitle} onClose={onClose} onBack={handleBack} enableSnap {...props}>
      {!showColors && !showTexture && !showUploadsTab && (
        <>
          <div className={Styles.imagesAndColors}>
            <ImageButtons
              onSelectUploads={() => {
                // Need to set a small timeout, otherwise the 'click' bleeds through and selects
                // the image that happens to be in that same spot where the screen was tapped
                setTimeout(() => {
                  setShowUploadsTab(true)
                }, 100)
              }}
            />
            <ColorsRow />
          </div>
          <Suggestions
            onExpand={(name, seeds) => {
              // Need to set a small timeout, otherwise the 'click' bleeds through and selects
              // the texture that happens to be in that same spot where the screen was tapped
              setTimeout(() => {
                openBackgroundsPanel({ panel: 'category', panelProps: { name, query: name, seeds } })
              }, 100)
            }}
          />
        </>
      )}
      {showColors && (
        <div className={Styles.colorsPanel}>
          <ColorsPanelContainer />
        </div>
      )}

      {showUploadsTab && <InnerDrawer />}

      {showTexture && (
        <div className={Styles.colorsPanel}>
          <Search query={texturePanelProps.name} onSelect={onTextureSelect} seeds={texturePanelProps.seeds} />
        </div>
      )}
    </DesignerDrawer>
  )
})

const BackgroundsDrawerContainer = (props: DrawerProps) => {
  return (
    <BackgroundsTabProvider>
      <UploadsTabProvider forBackground>
        <BackgroundsDrawer {...props} />
      </UploadsTabProvider>
    </BackgroundsTabProvider>
  )
}

export { BackgroundsDrawerContainer as BackgroundsDrawer }

BackgroundsDrawer.displayName = 'BackgroundsDrawer'
